import { create } from 'zustand'
import type { ContactType } from '@/lib/labels'

/** Contacts list chip: everyone, or one contact type (UX decision §5). */
export type ContactTypeFilter = 'all' | ContactType
/** Contacts list order: recently touched first, or A–Z by name. */
export type ContactSort = 'recent' | 'name'
export type ContactWorkFilter = 'all' | 'open' | 'won' | 'closed'

interface UIState {
  sidebarCollapsed: boolean
  mobileMenuOpen: boolean
  moreMenuOpen: boolean
  /** Global search (⌘K) — opened from the sidebar button, the phone header, or the shortcut. */
  searchOpen: boolean
  activeModal: string | null
  toasts: Toast[]
  // AC-21: true once the user has interacted with the page at least once,
  // satisfying the browser autoplay policy so the inbound ring tone can play.
  // In-memory only (resets per-session on full reload).
  hasUserInteracted: boolean
  inboxBadge: number
  contactTypeFilter: ContactTypeFilter
  contactSort: ContactSort
  contactWorkFilters: Record<string, ContactWorkFilter>
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileMenu: () => void
  setMoreMenuOpen: (open: boolean) => void
  setSearchOpen: (open: boolean) => void
  toggleSearch: () => void
  openModal: (modalId: string) => void
  closeModal: () => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  markUserInteracted: () => void
  setInboxBadge: (count: number) => void
  setContactTypeFilter: (filter: ContactTypeFilter) => void
  setContactSort: (sort: ContactSort) => void
  setContactWorkFilter: (contactId: string, filter: ContactWorkFilter) => void
}

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  /** One line, names the thing: "Quote sent to Brad Holloway". */
  title: string
  message?: string
  /** ms; default 4000 (6000 for errors). */
  duration?: number
  /** One follow-up, e.g. { label: 'Open', onClick }. */
  action?: { label: string; onClick: () => void }
}

/** At most this many toasts on screen; the oldest goes first. */
const MAX_TOASTS = 3

let toastCounter = 0
const toastTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  moreMenuOpen: false,
  searchOpen: false,
  activeModal: null,
  toasts: [],
  hasUserInteracted: false,
  inboxBadge: 0,
  contactTypeFilter: 'all',
  contactSort: 'recent',
  contactWorkFilters: {},
  markUserInteracted: () => set({ hasUserInteracted: true }),
  setInboxBadge: (count) => set({ inboxBadge: count }),
  setContactTypeFilter: (filter) => set({ contactTypeFilter: filter }),
  setContactSort: (sort) => set({ contactSort: sort }),
  setContactWorkFilter: (contactId, filter) =>
    set((state) => ({
      contactWorkFilters: { ...state.contactWorkFilters, [contactId]: filter },
    })),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
  setMoreMenuOpen: (open: boolean) => set({ moreMenuOpen: open }),
  setSearchOpen: (open) => set({ searchOpen: open }),
  toggleSearch: () => set((state) => ({ searchOpen: !state.searchOpen })),
  openModal: (modalId) => set({ activeModal: modalId }),
  closeModal: () => set({ activeModal: null }),
  addToast: (toast) => {
    const id = `toast-${++toastCounter}`
    set((state) => {
      // Same toast already showing (e.g. two failed saves in a row): don't stack a copy.
      const others = state.toasts.filter((t) => !(t.title === toast.title && t.message === toast.message))
      const next = [...others, { ...toast, id }]
      for (const dropped of next.slice(0, Math.max(0, next.length - MAX_TOASTS))) {
        const t = toastTimers.get(dropped.id)
        if (t !== undefined) { clearTimeout(t); toastTimers.delete(dropped.id) }
      }
      return { toasts: next.slice(-MAX_TOASTS) }
    })
    const timer = setTimeout(() => {
      toastTimers.delete(id)
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
    }, toast.duration ?? (toast.type === 'error' ? 6000 : 4000))
    toastTimers.set(id, timer)
  },
  removeToast: (id) => {
    const timer = toastTimers.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      toastTimers.delete(id)
    }
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },
}))
